import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Element, Icon } from '../../atoms';
import styles from './pagination.styles';
import Context from './context';
import Item from './components/item';

export const calculateStartPage = (current, total, totalToDisplay) => {
  let starts = 0;

  if (current >= Math.floor(totalToDisplay / 2)) {
    starts = current - Math.ceil(totalToDisplay / 2);
  }

  if (starts + totalToDisplay >= total) {
    starts = total - totalToDisplay;
  }

  return Math.max(0, starts) + 1;
};

const Pagination = React.forwardRef(
  (
    {
      total = 1,
      current,
      className,
      itemProps = (page) => {
        return { as: 'a', href: page };
      },
      children,
      onChange,
      onClick,
      ...props
    },
    ref,
  ) => {
    const totalToDisplay = 3;
    let starts = 0;
    // if children its pass its a custom pagination, so do not validate pages
    if (!children && (current < 0 || current > total)) {
      console.warn(`Prop current value (${current}) its invalid should be between 0 and ${total}`);
    }

    if (current >= Math.floor(totalToDisplay / 2)) {
      starts = current - Math.ceil(totalToDisplay / 2);
    }

    if (starts + totalToDisplay >= total) {
      starts = total - totalToDisplay;
    }

    starts = calculateStartPage(current, total, totalToDisplay);

    const contextValue = useMemo(() => {
      return {
        current,
        total,
        totalToDisplay,
        starts,
      };
    }, [current, total, totalToDisplay, starts]);

    const onChangeHandler = useCallback(
      (evt) => {
        onClick?.(evt);

        const target = evt.target.closest('[data-page]');
        if (!target?.dataset?.page) {
          return;
        }
        if (target.getAttributeNames().includes('disabled')) {
          return;
        }
        target.value = parseInt(target.dataset.page, 10);
        evt.target = target;
        onChange?.(evt);
      },
      [onChange, onClick],
    );

    if (total === 1) {
      return null;
    }
    return (
      <Element onClick={onChangeHandler} as="nav" className={clsx(styles.root, className)} ref={ref} {...props}>
        <Context.Provider value={contextValue}>
          {children || (
            <>
              <Item data-testid="go-to-prev" hideActive disabled={current === 1} page={current - 1} {...itemProps?.(current - 1)}>
                <Icon name="chevron-left" />
              </Item>
              {starts > 1 && (
                <Item page={1} {...itemProps?.(1)}>
                  1
                </Item>
              )}
              {starts > 2 && <Item disabled>...</Item>}
              {Array.from({ length: Math.min(total, totalToDisplay) }, (_, i) => {
                const page = starts + i;
                return (
                  <Item page={page} key={page} {...itemProps?.(page)}>
                    {page}
                  </Item>
                );
              })}
              {starts + totalToDisplay < total && <Item disabled>...</Item>}

              {starts + totalToDisplay - 1 < total && (
                <Item page={total} {...itemProps?.(total)}>
                  {total}
                </Item>
              )}
              <Item data-testid="go-to-next" hideActive page={current + 1} disabled={current === total} {...itemProps?.(current + 1)}>
                <Icon name="chevron-right" />
              </Item>
            </>
          )}
        </Context.Provider>
      </Element>
    );
  },
);

Pagination.Item = Item;
Pagination.calculateStartPage = calculateStartPage;

export default Pagination;
